import React, { useCallback, useState, useEffect, useMemo } from 'react'

import { AuthLogEntry, Order, Page } from '../../types'
import { fetchAuthLogs } from '../../api/API'
import { useDebounce } from 'use-debounce/lib'
import AuthLogRow from './components/AuthLogRow'
import AuthLogFilters from './components/AuthLogFilters'
import StyledTableCell from '../../components/StyledTableCell'
import RefreshButton from '../../components/RefreshButton'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'

const columns = [
  { key: 'loginTime', label: 'Login Time' },
  { key: 'username', label: 'Username' },
  { key: 'email', label: 'Email' },
  { key: 'application', label: 'Application' },
]

const AuthLog: React.FC = () => {
  const [data, setData] = useState<Page<AuthLogEntry> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [sortDirection, setSortDirection] = useState<'DESC' | 'ASC'>('DESC')

  const [nameFilter, setNameFilter] = useState('')
  const [nameFilterDebounced] = useDebounce(nameFilter, 300, { leading: false })

  const [emailFilter, setEmailFilter] = useState('')
  const [emailFilterDebounced] = useDebounce(emailFilter, 300, { leading: false })

  const [application, setApplication] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const dateRangeError = dateFrom && dateTo && dateFrom > dateTo

  const fetchCallback = useCallback(() => {
    if (dateRangeError) return

    setLoading(true)
    const loginTimeFrom = dateFrom ? new Date(dateFrom).toISOString() : undefined
    const loginTimeTo = dateTo ? new Date(dateTo).toISOString() : undefined

    fetchAuthLogs(
      page,
      rowsPerPage,
      'loginTime',
      sortDirection,
      nameFilterDebounced || undefined,
      emailFilterDebounced || undefined,
      application || undefined,
      loginTimeFrom,
      loginTimeTo
    )
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((error) => setError(error.message))
      .finally(() => {
        setLoading(false)
      })

    //eslint-disable-next-line
  }, [page, rowsPerPage, sortDirection, nameFilterDebounced, emailFilterDebounced, application, dateFrom, dateTo])

  useEffect(fetchCallback, [fetchCallback])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'))
    setPage(0)
  }

  const handleNameFilterChange = (value: string) => {
    setNameFilter(value)
    setPage(0)
  }

  const handleEmailFilterChange = (value: string) => {
    setEmailFilter(value)
    setPage(0)
  }

  const handleApplicationChange = (value: string) => {
    setApplication(value)
    setPage(0)
  }

  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    setPage(0)
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    setPage(0)
  }

  const handleClearFilters = () => {
    setNameFilter('')
    setEmailFilter('')
    setApplication('')
    setDateFrom('')
    setDateTo('')
    setPage(0)
  }

  const rows = useMemo(
    () => data?.content.map((entry) => <AuthLogRow key={entry.referenceId} entry={entry} />) ?? [],
    [data]
  )

  const isEmpty = data != null && data.content.length === 0

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Typography variant="h4" color="primary">
          Authentication Log
        </Typography>
        {useMediaQuery((theme: Theme) => theme.breakpoints.up('sm')) && (
          <RefreshButton loading={loading} refresh={fetchCallback} />
        )}
      </Box>

      <AuthLogFilters
        nameFilter={nameFilter}
        emailFilter={emailFilter}
        application={application}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onNameFilterChange={handleNameFilterChange}
        onEmailFilterChange={handleEmailFilterChange}
        onApplicationChange={handleApplicationChange}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
        onClear={handleClearFilters}
      />

      {dateRangeError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          "From" date must be before "To" date.
        </Alert>
      )}

      <Box>
        {loading ? (
          Array.from(Array(6)).map((_, idx) => <Skeleton key={`skeleton-${idx}`} height="82px" animation="wave" />)
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchCallback}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Error fetching authentication logs</AlertTitle>
            {error}
          </Alert>
        ) : isEmpty ? (
          <Alert severity="info">
            <AlertTitle>No login events found</AlertTitle>
            Try adjusting your filters or{' '}
            <Button color="inherit" size="small" onClick={handleClearFilters}>
              clear all filters
            </Button>
          </Alert>
        ) : (
          <Paper>
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <StyledTableCell key={col.key}>
                        {col.key === 'loginTime' ? (
                          <TableSortLabel
                            active
                            direction={sortDirection === 'DESC' ? 'desc' : 'asc'}
                            onClick={handleSortToggle}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={data?.totalElements || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default AuthLog
